<app-dossier
  eyebrow="Dossier agent // Ordre de mission"
  [stamp]="view() === 'threatened' ? 'Urgent' : 'Top secret'"
  [reference]="operation.value()?.codeName ?? null"
>
  <app-page-title [title]="title()" [lede]="lede()" />

  @if (target.value(); as target) {
    @if (view() === 'threatened') {
      @if (threat.value(); as threat) {
        <div role="alert">
          <app-target-card
            heading="Un agent déclare vous avoir éliminé"
            icon="warning"
            [portrait]="false"
            actionLabel="Méthode d'élimination"
            [action]="threat.action.description"
          >
            <p class="panel__text">
              Si c'est exact, confirmez votre élimination. Sinon, contestez : la déclaration sera
              annulée et la partie continue.
            </p>
            <app-countdown
              [until]="threat.expiresAt"
              label="Sans réponse, élimination validée dans"
              [compact]="true"
            />
            <div class="button-row">
              <button
                class="button button--secondary"
                type="button"
                [disabled]="busy()"
                (click)="contest()"
              >
                Contester
              </button>
              <button
                class="button button--danger"
                type="button"
                [disabled]="busy()"
                (click)="confirm()"
              >
                Confirmer
              </button>
            </div>
          </app-target-card>
        </div>
      }
      <h2 class="section-label">Votre ordre de mission</h2>
    }

    <app-target-card
      heading="Votre cible"
      [name]="target.agent.firstName"
      [aside]="'alias ' + target.agent.codeName"
      [actionLabel]="view() === 'claimed' ? 'Action réalisée' : 'Action à réaliser'"
      [action]="target.action.description"
      [stamp]="view() === 'claimed' ? 'En attente de validation' : null"
    />

    @if (view() === 'claimed') {
      @if (claimDeadline(); as deadline) {
        <div class="spaced">
          <app-countdown [until]="deadline" label="Validation automatique dans" [compact]="true" />
        </div>
      }
      <div class="banner">
        <img src="/assets/lock.svg" alt="" />
        <p>
          Si la cible conteste, l'élimination est annulée et votre ordre de mission reste inchangé.
          <span class="banner__accent">Sinon, vous hériterez de sa cible.</span>
        </p>
      </div>
      <div class="form spaced">
        <button
          class="button button--secondary button--block"
          type="button"
          [disabled]="busy()"
          (click)="cancelElimination()"
        >
          Annuler la déclaration
        </button>
        <p class="field__hint center">
          Déclaration faite par erreur ? Retirez-la avant sa validation : votre ordre de mission
          reste inchangé.
        </p>
      </div>
    } @else if (view() === 'orders') {
      <div class="form spaced">
        <button
          class="button button--primary button--block"
          type="button"
          [disabled]="busy()"
          (click)="confirmingElimination.set(true)"
        >
          Cible neutralisée <span class="button__arrow" aria-hidden="true">›</span>
        </button>
        <p class="field__hint center">
          Déclarez l'élimination une fois l'action réalisée. Votre cible aura une minute pour la
          confirmer ou la contester.
        </p>
        <button
          class="button button--secondary button--block"
          type="button"
          [disabled]="busy() || actionChangeLockedUntil() !== null"
          (click)="changeAction()"
        >
          Changer d'action
        </button>
        @if (actionChangeLockedUntil(); as until) {
          <app-countdown
            [until]="until"
            label="Nouveau changement possible dans"
            [compact]="true"
          />
        } @else {
          <p class="field__hint center">
            Action impossible à réaliser ? Le QG vous en attribue une autre au hasard, une fois
            toutes les 5 minutes.
          </p>
        }
      </div>
    }

    @if (commandError(); as message) {
      <p class="field__error center" role="alert">{{ message }}</p>
    }

    <h2 class="section-label">État de la partie</h2>
    <app-game-status status="En cours" [counts]="counts()"
      >Restez discret. La cible ne sait pas qui la traque.</app-game-status
    >

    <section class="card spaced service" aria-labelledby="service-title">
      <div class="card__header">
        <h2 class="card__title" id="service-title">États de service</h2>
        @if (codeName(); as codeName) {
          <span>Agent {{ codeName }}</span>
        }
      </div>
      <dl class="facts">
        <dt>Cibles neutralisées</dt>
        <dd>{{ score() }}</dd>
        @if (mission.value()?.startedAt; as startedAt) {
          <dt>En mission depuis</dt>
          <dd>{{ startedAt | date: "HH 'h' mm" }}</dd>
        }
      </dl>
      <img class="paperclip paperclip--bottom" src="/assets/paperclip.svg" alt="" />
    </section>

    <p class="center dossier__footer">
      <button class="button button--link" type="button" (click)="confirmingAbandon.set(true)">
        Abandonner la mission
      </button>
    </p>
  } @else if (loadError(); as message) {
    <app-state-panel kind="error" title="Transmission impossible" [message]="message">
      <button
        class="button button--secondary"
        type="button"
        (click)="target.reload(); mission.reload()"
      >
        Réessayer
      </button>
    </app-state-panel>
  } @else {
    <app-state-panel
      kind="loading"
      title="Liaison avec le QG…"
      message="Récupération de votre ordre de mission."
    />
  }

  <app-confirm-dialog
    [open]="confirmingElimination()"
    title="Déclarer l'élimination ?"
    confirmLabel="Déclarer l'élimination"
    cancelLabel="Pas encore"
    [busy]="busy()"
    (confirmed)="eliminate()"
    (cancelled)="confirmingElimination.set(false)"
  >
    <p>Ne déclarez l'élimination qu'une fois l'action réalisée.</p>
    <p class="accent">Votre cible aura une minute pour la confirmer ou la contester.</p>
    <p>Sans réponse de sa part, l'élimination sera validée.</p>
  </app-confirm-dialog>

  <app-confirm-dialog
    [open]="confirmingAbandon()"
    title="Abandonner la mission ?"
    confirmLabel="Abandonner"
    cancelLabel="Poursuivre la mission"
    [busy]="busy()"
    (confirmed)="abandon()"
    (cancelled)="confirmingAbandon.set(false)"
  >
    <p>Vous quitterez la partie pour de bon : votre chasseur héritera de votre cible.</p>
    <p>Si une élimination est en attente contre vous, elle sera validée.</p>
    <p class="accent">Cette décision est définitive.</p>
  </app-confirm-dialog>
</app-dossier>
